import { getGenerativeModel } from 'firebase/ai';

import { ai } from '../../../config/firebaseConfig.js';
import {
  AI_RESPONSE_LIMITS,
  DEFAULT_AI_GENERATION_CONFIG,
  DEFAULT_AI_MODEL_NAME,
} from '../config/aiModelConfig.js';
import {
  PORTFOLIO_SCORE_CHECKLIST,
  getPortfolioChecklistMaxScore,
} from '../config/portfolioChecklistConfig.js';
import aiContext from '../config/AIGenerationContext.md?raw';
import aiExample from '../config/aiExample.json';
import {
  projectRewriteSchema,
  recommendationSchema,
  skillRewriteSchema,
} from '../schemas/contentSchemas.js';
import {
  scoreOutputSchema,
  scoreOutputWithDescriptionsSchema,
} from '../schemas/portfolioScoringSchemas.js';
import { assertCompleteChecklistDescriptions } from './scoreValidation.js';
import { updateImageDescription } from '../../Common/Images/utilities/imageUtils.js';

const DEFAULT_PADDING = {
  paddingT: 'pt-0',
  paddingB: 'pb-2',
  paddingL: 'ps-0',
  paddingR: 'pe-0',
};

function ensureAIClient() {
  if (!ai) {
    throw new Error('Firebase AI Logic is not initialized.');
  }

  return ai;
}

function createModel(generationConfig = {}, modelName = DEFAULT_AI_MODEL_NAME) {
  return getGenerativeModel(ensureAIClient(), {
    model: modelName,
    generationConfig: {
      ...DEFAULT_AI_GENERATION_CONFIG,
      ...generationConfig,
    },
  });
}

function parseJsonString(rawText = '') {
  const trimmed = String(rawText || '').trim();
  if (!trimmed) {
    throw new Error('AI returned empty response.');
  }

  try {
    return JSON.parse(trimmed);
  } catch (_) {
    const withoutCodeFence = trimmed
      .replace(/^```json\s*/i, '')
      .replace(/^```\s*/i, '')
      .replace(/\s*```$/, '');

    return JSON.parse(withoutCodeFence);
  }
}

async function generateStructuredJson({
  prompt,
  maxOutputTokens = 8192,
  responseSchema,
  modelName = DEFAULT_AI_MODEL_NAME,
}) {
  const baseModelOptions = {
    maxOutputTokens,
    responseMimeType: 'application/json',
  };

  const tryGenerate = async (options) => {
    const model = createModel(options, modelName);
    const result = await model.generateContent(prompt);
    const responseText = result?.response?.text?.() || '';
    return parseJsonString(responseText);
  };

  if (responseSchema) {
    try {
      return await tryGenerate({
        ...baseModelOptions,
        responseSchema,
      });
    } catch (err) {
      const message = asTrimmed(err?.message).toLowerCase();
      if (message.includes('tojson')) {
        console.warn('Falling back to schema-less JSON mode due to SDK schema serialization issue.', err);
      } else {
        throw err;
      }
    }
  }

  return tryGenerate(baseModelOptions);
}

async function generateImageDescription(image) {
  const model = createModel({ maxOutputTokens: 256 });

  // The Gemini model expects a specific format for inline data.
  const imagePart = {
    inlineData: {
      data: image.base64.split(',')[1], // Remove the data URI prefix
      mimeType: image.type,
    },
  };

  const prompt = 'Briefly describe this image in 5-10 words. This description will be used to help an AI portfolio generator select the right image. Focus on the subject and style.';

  try {
    const result = await model.generateContent([prompt, imagePart]);
    return asTrimmed(result?.response?.text?.());
  } catch (err) {
    console.warn(`Could not generate description for image ${image.id}:`, err);
    return ''; // Return an empty string on failure
  }
}

export async function generateImageDescriptions(images = [], userId) {
  console.log('Images received by generateImageDescriptions:', images);
  const imagesToDescribe = images
    .filter(img => img.id && img.base64 && !img.description) // Only process images that need a description
    .slice(0, AI_RESPONSE_LIMITS.maxImagesToDescribe); // Limit the number of images processed at once

  console.log('Images selected for description generation (filtered):', imagesToDescribe);

  if (imagesToDescribe.length === 0) {
    return images; // Return original array if no work is needed
  }

  // Create a batch of promises to generate descriptions in parallel
  const descriptionPromises = imagesToDescribe.map(async (image) => {
    const description = await generateImageDescription(image);
    // Save the description to Firestore immediately
    if (description) {
      await updateImageDescription(userId, image.id, description);
    }
    return {
      ...image,
      description: description || 'No description available',
    };
  });

  const describedImages = await Promise.all(descriptionPromises);

  // Merge the newly described images back into the original array
  const imageMap = new Map(describedImages.map(img => [img.id, img]));
  const updatedImages = images.map(img => imageMap.get(img.id) || img);

  return updatedImages;
}

function asTrimmed(value) {
 return String(value || '').trim();
}

function withDefaultPadding(item) {
 return {
  ...DEFAULT_PADDING,
  ...item,
 };
}

function normalizeColumnType(type) {
 return ['col-2', 'col-3', 'col-4'].includes(type) ? type : 'col-2';
}

function normalizeLeafItem(item) {
  const local = item && typeof item === 'object' ? item : {};
  const type = asTrimmed(local.type);

  if (type === 'title' || type === 'subtitle' || type === 'paragraph') {
    return withDefaultPadding({
      ...local,
      type,
      'text-content': asTrimmed(local['text-content']) || (type === 'title' ? 'Untitled section' : 'Add your content here.'),
    });
  }

  if (type === 'ul' || type === 'ol') {
    const list = Array.isArray(local.list)
      ? local.list.map((entry) => asTrimmed(entry)).filter(Boolean)
      : [];

    return withDefaultPadding({
      ...local,
      type,
      style: asTrimmed(local.style) || 'initial',
      list: list.length > 0 ? list : ['Item 1', 'Item 2'],
    });
  }

  if (type === 'link') {
    return withDefaultPadding({
      ...local,
      type: 'link',
      text: asTrimmed(local.text) || 'Learn more',
      href: asTrimmed(local.href),
      variant: asTrimmed(local.variant) || 'primary',
      size: asTrimmed(local.size) || 'md',
      align: asTrimmed(local.align) || 'left',
      openInNewTab: local.openInNewTab !== false,
    });
  }

  return withDefaultPadding({
    type: 'paragraph',
    text: asTrimmed(local.text) || 'Add your content here.',
  });
}

function normalizeColumns(item) {
  const colType = normalizeColumnType(asTrimmed(item.type));
  const expectedCount = Number(colType.replace('col-', '')) || 2;
  const rawColumns = Array.isArray(item.columns) ? item.columns : [];

  const normalizedColumns = [];
  for (let i = 0; i < expectedCount; i += 1) {
    const current = rawColumns[i];
    const currentContent = Array.isArray(current?.content) ? current.content : [];
    const first = normalizeItem(currentContent[0]);

    normalizedColumns.push({
      content: [first],
    });
  }

  return withDefaultPadding({
    ...item,
    type: colType,
    columns: normalizedColumns,
  });
}

function normalizeSection(item) {
  const rawContent = Array.isArray(item.content) ? item.content : [];
  let content = rawContent
    .map((entry) => normalizeItem(entry))
    .filter(Boolean)
    .slice(0, AI_RESPONSE_LIMITS.maxPortfolioItems);

  // If the AI provides a title string on the section, create a title component for it
  const sectionTitle = asTrimmed(item.title);
  if (sectionTitle) {
    const titleComponent = normalizeLeafItem({
      type: 'title',
      'text-content': sectionTitle,
    });
    content = [titleComponent, ...content];
  }

  return {
    type: 'section',
    title: '', // This is now handled inside content
    content,
  };
}

function normalizeItem(item) {
  const local = item && typeof item === 'object' ? item : {};
  const type = asTrimmed(local.type);

  if (type === 'section') {
    return normalizeSection(local);
  }

  if (type === 'col-2' || type === 'col-3' || type === 'col-4') {
    return normalizeColumns(local);
  }

  if (type === 'image') {
    return withDefaultPadding({
      ...local,
      type: 'image',
      imageId: asTrimmed(local.imageId),
      'aspect-ratio': asTrimmed(local['aspect-ratio']) || '16 / 9',
    });
  }

  if (type === 'carousel') {
    const images = Array.isArray(local.images)
      ? local.images.map((img) => ({ imageId: asTrimmed(img.imageId) }))
      : [];

    return withDefaultPadding({
      ...local,
      type: 'carousel',
      images,
      title: asTrimmed(local.title),
      subtitle: asTrimmed(local.subtitle),
      fullWidth: local.fullWidth === true,
      showControls: local.showControls !== false,
      autoScroll: local.autoScroll !== false,
      autoScrollInterval: Number(local.autoScrollInterval) || 5000,
      'aspect-ratio': asTrimmed(local['aspect-ratio']) || '16 / 9',
    });
  }

  if (type === 'project') {
    return withDefaultPadding({
      ...local,
      type: 'project',
      projectId: asTrimmed(local.projectId),
    });
  }

  if (type === 'skills') {
    return withDefaultPadding({
      ...local,
      type: 'skills',
      skillIds: Array.isArray(local.skillIds) ? local.skillIds.map(asTrimmed) : [],
    });
  }

  return normalizeLeafItem(local);
}

function normalizePortfolioOutput(payload, fallbackTitle = 'AI Portfolio Draft') {
  const safePayload = payload && typeof payload === 'object' ? payload : {};
  const rawContent = Array.isArray(safePayload.content) ? safePayload.content : [];

  const content = rawContent
    .map((entry) => normalizeItem(entry))
    .filter(Boolean)
    .slice(0, AI_RESPONSE_LIMITS.maxPortfolioItems);

  const hydratedContent =
    content.length > 0
      ? content
      : [
          withDefaultPadding({
            type: 'title',
            text: fallbackTitle,
          }),
          withDefaultPadding({
            type: 'paragraph',
            text: 'No valid content was generated. Try refining your prompt and regenerate.',
          }),
        ];

  return {
    title: asTrimmed(safePayload.title) || fallbackTitle,
    theme: asTrimmed(safePayload.theme) || 'Default',
    content: hydratedContent,
  };
}

function summarizeProjects(projects = []) {
  return projects
    .slice(0, AI_RESPONSE_LIMITS.maxProjects)
    .map((p) => {
      const projectImages = Array.isArray(p.images) && p.images.length > 0
        ? p.images
            .map((image, index) => {
              const imageId = image?.imageId || image?.id || image?.filename || `image-${index + 1}`;
              const description = asTrimmed(image?.description) || 'No image description provided';
              return `${imageId}: ${description}`;
            })
            .join('; ')
        : 'No project images listed';

      return [
        `- ${asTrimmed(p.title) || 'Untitled project'} (projectId: ${p.id})`,
        `  Overview: ${asTrimmed(p.overview || p.description) || 'No overview provided'}`,
        `  Project URL: ${asTrimmed(p.projectUrl || p.linkUrl) || 'No URL provided'}`,
        `  Images: ${projectImages}`,
      ].join('\n');
    })
    .join('\n');
}

function summarizeSkills(skills = []) {
  return skills
    .slice(0, AI_RESPONSE_LIMITS.maxSkills)
    .map((s) => [
      `- ${asTrimmed(s.name) || 'Unnamed skill'} (ID: ${s.id})`,
      `  Description: ${asTrimmed(s.description) || 'No description provided'}`,
      `  Rating: ${Number.isFinite(Number(s.rating)) ? `${s.rating}/5` : 'No rating provided'}`,
    ].join('\n'))
    .join('\n');
}

function summarizeImages(images = []) {
  console.log('Summarizing images for AI prompt:', images);
  return images
    .slice(0, AI_RESPONSE_LIMITS.maxImages)
    .map((i) => `- Filename: ${i.filename} (ID: ${i.id}), Description: ${i.description || 'N/A'}`)
    .join('\n');
}

function summarizeJobs(jobs = []) {
  return jobs
    .slice(0, AI_RESPONSE_LIMITS.maxJobs)
    .map((j) => `- ${j.title} (ID: ${j.id})`)
    .join('\n');
}

function buildPortfolioGenerationPrompt({ prompt, portfolio, projects = [], skills = [], jobs = [], images = [] }) {
  const projectSummary = summarizeProjects(projects);
  const skillSummary = summarizeSkills(skills);
  const imageSummary = summarizeImages(images);

  const instruction = portfolio
    ? "You are an expert portfolio designer. The user wants to improve their existing portfolio. Modify the provided 'Current Portfolio JSON' based on the user's request. Return the complete, updated JSON object."
    : "You are an expert portfolio designer. Generate a new portfolio structure in JSON format based on the user's request and the available context.";

  let context = `${instruction}\n\nFollow the JSON schema and rules defined in the "JSON Structure Guide" section.\n\nHere is the user's request:\n---\n${prompt}\n---\n\n`;

  if (portfolio) {
    context += `Current Portfolio JSON:\n${JSON.stringify(portfolio, null, 2)}\n---\n\n`;
  }

  context += `Here is the available context you can use. Reference items by their ID and preserve the user's saved data accurately.\n\nAvailable Projects:\n${projectSummary || 'No projects provided.'}\n\nAvailable Skills:\n${skillSummary || 'No skills provided.'}\n\nAvailable Images (for carousels and image components):\n${imageSummary || 'No images provided.'}\n\n`;

  context += `NOTE: In the 'Full JSON Example' below, any \`imageId\`s are placeholders. You MUST replace them with actual \`imageId\`s from the 'Available Images' list provided above, choosing the most relevant image based on its description.\n\n---\nJSON Structure Guide:\n${aiContext}\n\n---\nFull JSON Example:\n${JSON.stringify(aiExample, null, 2)}\n\nIMPORTANT INSTRUCTIONS:\n1. NEVER use placeholder text like "[Skill 1]", "[Skill 2]", "[mention types]", "[achievement]", or similar brackets. These are forbidden.\n2. ONLY use actual data from the "Available Projects", "Available Skills", and user's provided information.\n3. Every portfolio must include at least one \`project\` component and at least one \`skills\` component using IDs from the available context.\n4. Include every project from "Available Projects" as a \`project\` component using its exact \`projectId\`, unless there are no projects provided.\n5. When presenting a saved project, ALWAYS use a \`project\` component. Do not replace saved project components with only text, images, lists, columns, or links.\n6. Include every skill from "Available Skills" in one or more \`skills\` components using exact \`skillIds\`, unless there are no skills provided.\n7. If a full-width \`carousel\` is at the top of the portfolio or top of a major section, set \`showControls\` to \`false\`.\n8. Never place two \`title\` items directly after each other. Use \`title\` then \`subtitle\` then \`paragraph\`, or \`title\` then \`paragraph\`.\n9. If you are writing paragraphs, use the real names, descriptions, ratings, URLs, image descriptions, and project overviews from the available context, not generic placeholders.\n10. When creating skill descriptions or project summaries, reference the actual data provided - combine multiple skills/projects if needed to create natural, flowing text.\n11. NEVER generate fake data or use template text with placeholders. If specific information is missing, write more general professional text based on what IS available.\n12. You MUST return only a single, valid JSON object that strictly follows the structure, types, and rules described in the 'JSON Structure Guide' and shown in the 'Full JSON Example'. Do not include any other text, explanations, or code fences in your response.`;

  console.log('Generated AI Prompt:', context); // Log the final prompt for debugging
  return context;
}

export async function generatePortfolioFromScratch({
  prompt,
  portfolio,
  projects = [],
  skills = [],
  jobs = [],
  images = [],
  fallbackTitle = 'AI Portfolio Draft',
}) {

  const fullPrompt = buildPortfolioGenerationPrompt({
    prompt,
    portfolio,
    projects,
    skills,
    jobs,    images, // Make sure images are passed here    images,
  });

  const raw = await generateStructuredJson({
    prompt: fullPrompt,
  });

  return normalizePortfolioOutput(raw, fallbackTitle);
}

export async function generateGeneralBlogFromPrompt({ prompt, portfolio, fallbackTitle = 'AI Blog Draft' }) {
  const userPrompt = asTrimmed(prompt);
  if (!userPrompt) {
    throw new Error('Prompt is required for blog generation.');
  }

  const fullPrompt = `Create a structured blog draft JSON for an editor.
Return valid JSON only and use only allowed types: title, subtitle, paragraph, ul, ol, link, section, col-2, col-3, col-4.
Keep it coherent and easy to edit.

Prompt:
${userPrompt}`;

  const raw = await generateStructuredJson({
    prompt: fullPrompt,
  });

  return normalizePortfolioOutput(raw, fallbackTitle);
}

export async function scorePortfolioAgainstChecklist({
  portfolioData,
  checklist = PORTFOLIO_SCORE_CHECKLIST,
  projects = [],
  skills = [],
  jobs = [],
  images = [],
  modelName = DEFAULT_AI_MODEL_NAME,
  checklistExplanationField = 'reason',
}) {
  const useChecklistDescriptions = checklistExplanationField === 'description';
  const safeChecklist = Array.isArray(checklist) ? checklist : [];
  const maxScore = safeChecklist.reduce((sum, entry) => sum + Number(entry.weight || 0), 0) || getPortfolioChecklistMaxScore();
  const checklistMetaById = new Map(
    safeChecklist.map((entry) => [asTrimmed(entry.id), entry])
  );
  const checklistWeights = new Map(
    safeChecklist.map((entry) => [asTrimmed(entry.id), Math.max(0, Number(entry.weight || 0))])
  );
  const contextScoringRules = Array.isArray(portfolioData?.scoringRules)
    ? portfolioData.scoringRules
      .map(asTrimmed)
      .filter(Boolean)
      .slice(0, 12)
    : [];
  const contextScoringInstructions = contextScoringRules
    .map((rule) => `- ${rule}`)
    .join('\n');

  const fullPrompt = `Evaluate this portfolio data against the checklist.
Return valid JSON only.

Checklist:
${JSON.stringify(safeChecklist)}

Candidate context:
Projects:
${summarizeProjects(projects) || 'None'}

Skills:
${summarizeSkills(skills) || 'None'}

Desired jobs:
${summarizeJobs(jobs) || 'None'}

Available images:
${summarizeImages(images) || 'None'}

Portfolio JSON:
${JSON.stringify(portfolioData || {})}

Scoring rules:
${contextScoringInstructions ? `${contextScoringInstructions}\n` : ''}- Return one checklist entry for every supplied checklist item, in the same order.
- Every checklist entry must contain id, passed, pointsAwarded, and ${useChecklistDescriptions ? 'description' : 'reason'}.
- pointsAwarded must be between 0 and checklist weight for each item.
- ${useChecklistDescriptions ? 'description' : 'reason'} is required, cannot be empty, and must briefly explain the evidence and why those points were awarded or withheld.
- score must equal sum(pointsAwarded).
- maxScore must be ${maxScore}.
- percentage must be score / maxScore * 100.
- Improvements must be specific and actionable.
- Use Candidate context (desired jobs, projects, skills, images) when forming recommendations.`;

  const result = await generateStructuredJson({
    prompt: fullPrompt,
    responseSchema: useChecklistDescriptions
      ? scoreOutputWithDescriptionsSchema
      : scoreOutputSchema,
    maxOutputTokens: 4096,
    modelName,
  });

  console.log(
    '[AI scoring] Structured JSON output:',
    JSON.stringify(result, null, 2)
  );

  const checklistResults = Array.isArray(result?.checklist)
    ? result.checklist
    : Array.isArray(result?.results)
      ? result.results
      : [];
  if (useChecklistDescriptions) {
    assertCompleteChecklistDescriptions(checklistResults, safeChecklist);
  }
  const normalizedChecklist = checklistResults
    .slice(0, AI_RESPONSE_LIMITS.maxChecklistFeedback)
    .map((entry) => {
      const id = asTrimmed(entry.id);
      const weight = checklistWeights.get(id) ?? 0;
      const rawPoints = Number(entry.pointsAwarded || 0);
      const pointsAwarded = Math.max(0, Math.min(weight, Number.isFinite(rawPoints) ? rawPoints : 0));
      const explanation = asTrimmed(
        useChecklistDescriptions ? entry.description : entry.reason
      );

      return {
        id,
        passed: typeof entry.passed === 'boolean' ? entry.passed : pointsAwarded >= weight,
        pointsAwarded,
        [useChecklistDescriptions ? 'description' : 'reason']: explanation,
      };
    })
    .filter((entry) => entry.id);

  const fallbackScore = Math.max(0, Math.min(maxScore, Number(result?.score || 0)));
  const computedScore = normalizedChecklist.reduce((sum, entry) => sum + entry.pointsAwarded, 0);
  const safeScore = normalizedChecklist.length > 0 ? computedScore : fallbackScore;
  const safePercentage = maxScore > 0 ? (safeScore / maxScore) * 100 : 0;
  const modelImprovements = Array.isArray(result?.improvements)
    ? result.improvements.map((item) => asTrimmed(item)).filter(Boolean)
    : [];

  const toRecommendationText = (text) => {
    const raw = asTrimmed(text);
    if (!raw) {
      return '';
    }

    const sentence = raw.endsWith('.') ? raw : `${raw}.`;
    return sentence;
  };

  const actionableRecommendationById = {
    'json-role-positioning': 'Clarify your role positioning at the top by stating target role, seniority, and a concise value proposition in title/subtitle text.',
    'json-project-evidence-quality': 'Strengthen project evidence by adding concrete problem, actions taken, tools used, and measurable outcomes next to project content.',
    'json-skills-traceability': 'Link listed skills to specific project outcomes by adding supporting paragraph evidence and validating non-empty skillIds.',
    'context-role-fit-score': 'Align portfolio content to desired jobs by emphasizing role-relevant strengths, proof points, and gaps to close for target positions.',
    'json-technical-depth': 'Add technical depth by describing architecture decisions, constraints, trade-offs, and implementation rationale in paragraph/list nodes.',
    'json-impact-metrics': 'Include measurable impact metrics such as percentages, scale, throughput, quality improvements, or time saved in text/list nodes.',
    'json-architecture-readability': 'Improve readability by organizing content into clear sections and balanced layout nodes instead of dense single blocks.',
    'context-image-narrative-support': 'Use images that reinforce technical outcomes and project credibility, and place them where they support surrounding narrative.',
    'json-cta-trust-links': 'Add a clear hiring call-to-action with meaningful links for contact, CV, GitHub, or portfolio proof.',
    'json-copy-quality-focus': 'Tighten copy by removing filler and using concise, role-focused language that highlights strengths and outcomes.',
  };

  const checklistImprovements = normalizedChecklist
    .filter((entry) => {
      const weight = checklistWeights.get(entry.id) ?? 0;
      return entry.pointsAwarded < weight;
    })
    .map((entry) => {
      const meta = checklistMetaById.get(entry.id) || {};
      const label = asTrimmed(meta.label) || entry.id;
      const description = asTrimmed(meta.description);

      const explanation = useChecklistDescriptions ? entry.description : entry.reason;
      if (explanation) {
        return toRecommendationText(explanation);
      }

      if (actionableRecommendationById[entry.id]) {
        return actionableRecommendationById[entry.id];
      }

      if (description) {
        return `Improve ${label.toLowerCase()} by applying this change: ${description}`;
      }

      return `Improve ${label}.`;
    });

  const normalizedModelImprovements = modelImprovements
    .map((item) => toRecommendationText(item))
    .filter(Boolean);

  const improvements = (useChecklistDescriptions
    ? normalizedModelImprovements
    : [...normalizedModelImprovements, ...checklistImprovements])
    .filter(Boolean)
    .filter((item, index, arr) => arr.indexOf(item) === index)
    .slice(0, AI_RESPONSE_LIMITS.maxChecklistFeedback);
  const recommendations = Array.isArray(result?.recommendations)
    ? result.recommendations
      .map((recommendation) => ({
        category: asTrimmed(recommendation?.category),
        title: asTrimmed(recommendation?.title),
        details: asTrimmed(recommendation?.details),
        priority: asTrimmed(recommendation?.priority),
      }))
      .filter((recommendation) => recommendation.title)
      .slice(0, 6)
    : [];

  const summary = asTrimmed(result?.summary) ||
    (improvements.length > 0
      ? `Found ${improvements.length} improvement area${improvements.length > 1 ? 's' : ''}.`
      : 'Portfolio checklist passed with no major gaps.');

  console.log('AI scoring normalized output:', {
    score: Math.max(0, Math.min(safeScore, maxScore)),
    maxScore,
    percentage: Math.max(0, Math.min(100, safePercentage)),
    checklistCount: normalizedChecklist.length,
    modelImprovements,
    checklistImprovements,
    improvements,
    summary,
  });

  return {
    score: Math.max(0, Math.min(safeScore, maxScore)),
    maxScore,
    percentage: Math.max(0, Math.min(100, safePercentage)),
    summary,
    checklist: normalizedChecklist,
    improvements,
    recommendations,
  };
}

export async function recommendProjectsAndSkills({
  projects = [],
  skills = [],
  jobs = [],
  request = '',
}) {
  if (!Array.isArray(jobs) || jobs.length === 0) {
    return {
      projects: [],
      skills: [],
    };
  }

  const MAX_RECOMMENDED_SKILL_CHARS = 80;
  const MAX_RECOMMENDED_SKILL_DESCRIPTION_CHARS = 140;
  const DISALLOWED_PROJECT_PATTERNS = [
    /personal\s+portfolio\s+website/i,
    /build\s+(a\s+)?portfolio\s+website/i,
    /create\s+(a\s+)?portfolio\s+website/i,
    /develop\s+(a\s+)?portfolio\s+website/i,
  ];

  const isDisallowedProjectRecommendation = (item) => {
    const title = asTrimmed(item?.title);
    const overview = asTrimmed(item?.overview);
    const combined = `${title} ${overview}`;
    return DISALLOWED_PROJECT_PATTERNS.some((pattern) => pattern.test(combined));
  };

  const trimTo = (value, maxChars) => {
    const text = asTrimmed(value);
    if (!text || text.length <= maxChars) {
      return text;
    }

    return `${text.slice(0, Math.max(0, maxChars - 1)).trimEnd()}...`;
  };

  const fullPrompt = `Recommend additional projects and skills for career growth.
Return valid JSON only.
Return an object with keys "projects" and "skills".

Current projects:
${summarizeProjects(projects) || 'None'}

Current skills:
${summarizeSkills(skills) || 'None'}

Desired jobs:
${summarizeJobs(jobs)}

User request:
${asTrimmed(request) || 'Recommend the most useful projects and skills to work on next.'}

Rules:
- Recommend only practical, resume-ready additions.
- Avoid duplicates against existing projects or skills.
- Keep each recommendation concise and specific.
- For every project, include 3 to 6 ordered "steps" written as concrete, achievable tasks.
- For every project, include one short "outcome" explaining what finished work should demonstrate.
- Order project steps from planning through implementation, testing, and presentation.
- Do NOT recommend creating/building/developing a personal portfolio website.
- Keep each suggested skill name under ${MAX_RECOMMENDED_SKILL_CHARS} characters.
- Keep each suggested skill description under ${MAX_RECOMMENDED_SKILL_DESCRIPTION_CHARS} characters.`;

  const result = await generateStructuredJson({
    prompt: fullPrompt, 
    responseSchema: recommendationSchema,
    maxOutputTokens: 4096,
  });

  console.log('AI recommendations raw output:', result);

  const projectCandidates = Array.isArray(result?.projects)
    ? result.projects
    : Array.isArray(result?.recommendedProjects)
      ? result.recommendedProjects
      : Array.isArray(result?.projectRecommendations)
        ? result.projectRecommendations
        : Array.isArray(result?.results?.projects)
          ? result.results.projects
          : Array.isArray(result?.recommendations?.projects)
            ? result.recommendations.projects
            : [];

  const skillCandidates = Array.isArray(result?.skills)
    ? result.skills
    : Array.isArray(result?.recommendedSkills)
      ? result.recommendedSkills
      : Array.isArray(result?.skillRecommendations)
        ? result.skillRecommendations
        : Array.isArray(result?.results?.skills)
          ? result.results.skills
          : Array.isArray(result?.recommendations?.skills)
            ? result.recommendations.skills
            : [];

  const suggestedProjects = Array.isArray(projectCandidates)
    ? projectCandidates
        .slice(0, AI_RESPONSE_LIMITS.maxRecommendations)
        .map((item) => {
          if (typeof item === 'string') {
            return {
              title: asTrimmed(item),
              overview: '',
              why: '',
              steps: [],
              outcome: '',
            };
          }

          return {
            title: asTrimmed(item.title || item.name || item.project || item.projectTitle),
            overview: asTrimmed(item.overview || item.description || item.summary),
            why: asTrimmed(item.why || item.rationale || item.reason),
            steps: (Array.isArray(item.steps)
              ? item.steps
              : Array.isArray(item.tasks) ? item.tasks : Array.isArray(item.checklist) ? item.checklist : [])
              .slice(0, 6)
              .map((step) => trimTo(
                typeof step === 'string' ? step : step?.task || step?.title || step?.description,
                220
              ))
              .filter(Boolean),
            outcome: trimTo(item.outcome || item.doneWhen || item.completion || item.deliverable, 300),
          };
        })
        .filter((item) => !isDisallowedProjectRecommendation(item))
        .filter((item) => item.title && item.overview)
    : [];

  const suggestedSkills = Array.isArray(skillCandidates)
    ? skillCandidates
        .slice(0, AI_RESPONSE_LIMITS.maxRecommendations)
        .map((item) => {
          if (typeof item === 'string') {
            return {
              name: trimTo(item, MAX_RECOMMENDED_SKILL_CHARS),
              description: '',
              why: '',
            };
          }

          return {
            name: trimTo(item.name || item.skill || item.title, MAX_RECOMMENDED_SKILL_CHARS),
            description: trimTo(item.description || item.overview || item.summary, MAX_RECOMMENDED_SKILL_DESCRIPTION_CHARS),
            why: asTrimmed(item.why || item.rationale || item.reason),
          };
        })
        .filter((item) => item.name)
    : [];

    console.log('AI recommendations output:', {
      suggestedProjects,
      suggestedSkills,
    });
  return {
    projects: suggestedProjects,
    skills: suggestedSkills,
  };
}

export async function rewriteProjectWithAI(projectData, request = '') {
  const prompt = `You are an expert copywriter. Rewrite the following project details to be more professional, engaging, and clear for a portfolio website. Keep the same meaning but enhance the impact.

Original Project:
Title: ${projectData.title}
Overview: ${projectData.overview}
Images:
${projectData.images.map(img => `- ID: ${img.imageId}, Current Description: ${img.description}`).join('\n')}

Requested improvement:
${asTrimmed(request) || 'Improve the wording while preserving the original meaning.'}

Instructions:
1. Return a JSON object with 'title', 'overview', and 'images' (an array of { imageId, description }).
2. Maintain the same imageId for each image.
3. The descriptions should be brief (10-20 words) and professional.
4. DO NOT use placeholders like [Skill Name] or [Company]. Use the actual information provided.
5. If the original info is sparse, use professional generalisations based on the context.`;

  return generateStructuredJson({
    prompt,
    responseSchema: projectRewriteSchema,
  });
}

export async function rewriteSkillWithAI(skillData, request = '') {
  const prompt = `Rewrite this skill for a student portfolio targeting a first job or apprenticeship.
Return valid JSON only with "name" and "description".

Current skill:
Name: ${asTrimmed(skillData?.name)}
Description: ${asTrimmed(skillData?.description)}

Requested improvement:
${asTrimmed(request) || 'Make the wording concise, professional, and evidence-focused.'}

Rules:
- Preserve the original meaning and do not invent experience, qualifications, or outcomes.
- Keep the skill name concise.
- Write a clear description suitable for an entry-level candidate.
- Do not return an icon, rating, ID, or any other field.`;

  const result = await generateStructuredJson({
    prompt,
    responseSchema: skillRewriteSchema,
    maxOutputTokens: 2048,
  });
  return {
    name: asTrimmed(result?.name).slice(0, 180),
    description: asTrimmed(result?.description).slice(0, 3000),
  };
}
